import moment from "moment";
import React, { useState } from "react";
import { FaFilePdf, FaPlus } from "react-icons/fa6";
import { LuUpload } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { getApiCaller, postApiCallerFormData } from "../../libs/apiCaller";
import { fetchProviderDetails } from "../commonFunctions/CommonFunctions";
import DocumentExpiredModal from "../modal/DocumentExpiredModal";

const ShowDocument = ({
  setOpenDocModal,
  documents = [],
  documentExpireDate,
  setChangeDocumentData,
  provider,
}) => {
  const [updatedDocuments, setUpdatedDocuments] = useState(documents);
  const [isUploading, setIsUploading] = useState(false);
  const [updateDocId, setUpdateDocId] = useState("");
  const dispatch = useDispatch();
  const [expiredDateOpenModal, setExpiredDateOpenModal] = useState(false);
  const [expiredDocDate, setExpiredDocDate] = useState("");
  const [expiredDocDateError, setExpiredDocDateError] = useState("");
  const [isDocUpdating, setIsDocUpdating] = useState(false);

  // Handle file change for a specific document
  const handleFileChange = async (index, event, docId) => {
    const file = event.target.files[0];
    if (!file) return;
    setUpdateDocId(docId);

    const updatedDocs = [...updatedDocuments];

    // Create a preview URL for image or file
    const objectUrl = URL.createObjectURL(file);
    updatedDocs[index] = {
      ...updatedDocs[index],
      file, // Store the file
      preview: objectUrl, // Store preview URL
    };

    setIsUploading(true);

    const formData = new FormData();
    formData.append("provider_id", provider?.id);
    formData.append("type", "add");
    formData.append("document_id", docId || "");
    if (file) {
      formData.append("document", file);
    }

    try {
      const response = await postApiCallerFormData(
        "provider_doc_update",
        formData
      );
      if (response.data?.status) {
        setUpdatedDocuments(updatedDocs);
        toast.success("Document added successfully");
        setUpdateDocId("");
        fetchProviderDetails(dispatch, provider?.id);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update document"
      );
    } finally {
      setIsUploading(false);
      setUpdateDocId("");
    }
  };

  const handleAction = async () => {
    if (!expiredDocDate) {
      setExpiredDocDateError("Please select an expiry date.");
      return;
    }

    const selectedDate = new Date(expiredDocDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setExpiredDocDateError("Expiry date cannot be in the past.");
      return;
    }

    setExpiredDocDateError("");
    setIsDocUpdating(true);
    const formattedDate = moment(expiredDocDate).format("DD-MM-YYYY");

    try {
      const response = await getApiCaller(
        `provider_doc_expiry?provider_id=${provider?.id}&expiry_date=${formattedDate}`
      );
      if (response?.data?.status) {
        toast.success(
          response?.data?.response_message ||
            "Provider Doc Expiry Date Update Successfully"
        );
        fetchProviderDetails(dispatch, provider?.id);
        setExpiredDocDate("");
        setExpiredDateOpenModal(false);
        setIsDocUpdating(false);
      }
    } catch (error) {
      setIsDocUpdating(false);
      toast.error(error?.message);
    }
  };

  return (
    <>
      <div className="border border-1 px-4 py-3 rounded-t-2xl text-xl mt-4 bg-white">
        <div className="flex justify-between">
          <p className="text-[#284184] font-bold">
            Documents (Expired Date :-{" "}
            <span className="text-[#f04438]">
              {documentExpireDate || "N/A"}
            </span>
            )
          </p>
          <button
            className="text-[#E0A256] text-base flex items-center space-x-1"
            onClick={() => setExpiredDateOpenModal(true)}
          >
            <FaPlus size={20} />
            <span className="underline">Add Document Expired Date</span>
          </button>
        </div>
      </div>

      <div className="border border-t-0 rounded-b-2xl px-4 py-4 bg-white">
        {updatedDocuments.length === 0 ? (
          <p className="text-center text-gray-500">No documents available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {updatedDocuments.map((doc, index) => {
              const fileExtension = doc?.file
                ? doc.file.name.split(".").pop().toLowerCase()
                : doc.path.split(".").pop().toLowerCase();

              return (
                <div
                  key={doc.id || index}
                  className="p-3 text-center border-r-2"
                >
                  <p className="font-semibold">
                    {doc.document_name}{" "}
                    <span className="text-red-600">
                      {" "}
                      {doc?.is_required ? "*" : ""}
                    </span>
                  </p>
                  <div className="my-3 justify-items-center">
                    {/* Show preview or original file */}
                    {doc.preview ? (
                      fileExtension === "pdf" ? (
                        <FaFilePdf className="text-red-600 w-[150px] h-[90px]" />
                      ) : (
                        <img
                          src={doc.preview}
                          className="my-3 rounded-md border w-[150px] h-[90px] object-cover"
                          alt="Updated Preview"
                        />
                      )
                    ) : fileExtension === "pdf" ? (
                      <FaFilePdf className="text-red-600 w-[150px] h-[90px]" />
                    ) : (
                      ["jpg", "jpeg", "png"].includes(fileExtension) && (
                        <img
                          src={doc.path}
                          className="my-3 rounded-md border w-[150px] h-[90px] object-cover"
                          alt={doc.document_name}
                        />
                      )
                    )}

                    {/* Upload New File */}
                    {!doc.path && doc.path === "" && !doc.preview && (
                      <div className="mt-2 flex justify-center w-[150px] h-[90px] items-center px-6 pt-3 pb-3 border-2 border-dashed border-[#E0A256] rounded-md">
                        {isUploading && updateDocId === doc?.id ? (
                          <BeatLoader color="#284184" />
                        ) : (
                          <div className="text-center">
                            <LuUpload className="mx-auto text-[#E0A256]" />
                            <label
                              htmlFor={`file-upload-${index}`}
                              className="relative cursor-pointer text-[#E0A256]"
                            >
                              Upload
                            </label>
                            <input
                              id={`file-upload-${index}`}
                              type="file"
                              className="sr-only"
                              onChange={(e) =>
                                handleFileChange(index, e, doc.id)
                              }
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {((doc.path && doc.path !== "") || doc.preview) && (
                    <div className="flex justify-center gap-5">
                      <a
                        href={doc.path || doc.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-[#E0A256]"
                      >
                        View Document
                      </a>
                      <button
                        onClick={() => {
                          setChangeDocumentData(doc); // Set the selected document data
                          setTimeout(() => setOpenDocModal(true), 0); // Open the modal after state update
                        }}
                        className="underline text-[#284184]"
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <DocumentExpiredModal
        onClose={() => {
          setExpiredDateOpenModal(false);
          setExpiredDocDateError("");
          setExpiredDocDate("");
        }}
        isOpen={expiredDateOpenModal}
        handleAction={handleAction}
        setExpiredDocDate={setExpiredDocDate}
        expiredDocDateError={expiredDocDateError}
        expiredDocDate={expiredDocDate}
        isDocUpdating={isDocUpdating}
      />
    </>
  );
};

export default ShowDocument;
